import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Box,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchMovingProductPreview } from "../../../redux/slices/dashSlice";
import {
  LocalOfferOutlined,
  CodeOutlined,
  CategoryOutlined,
  BarChartOutlined,
  DescriptionOutlined,
  WidgetsOutlined,
  FormatListNumberedOutlined,
  SellOutlined,
  PaletteOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import LoadingIndicator from "../../utils/LoadingIndicator";

const FastMovingProduct = forwardRef(
  ({ search, role, user, from, to, month, year, userOptions, selectedRegion, selectedState, selectedChannels}, ref) => {
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const { movingProductPreview, totalCount, getStatus } = useSelector(
      (state) => state.dash
    );

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
      dispatch(
        fetchMovingProductPreview({
          page: page + 1,
          rowsPerPage,
          search: search,
          role: role,
          user: user,
          from: from,
          to: to,
          month: from || to ? null : month,
          year: from || to ? null : year,
          selectedRegion: selectedRegion,
          selectedState: selectedState,
          selectedChannels: selectedChannels
        })
      );
    }, [dispatch, page, rowsPerPage, search, role, from, to, user, month, year, selectedRegion, selectedState, selectedChannels]);

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    useImperativeHandle(ref, () => ({
      downloadExcel: () => {
        if (!movingProductPreview || movingProductPreview.length === 0) return;

        const dataToExport = movingProductPreview.map((client, index) => ({
          "S.No.": index + 1,
          "SKU Code": client.sku_code,
          Quotations: client.quotation_count,
          Usage: client.usage_count,
          Brand: client.brand,
          Category: client.category,
          "MRP (₹)": client.mrp,
          Finish: client.finish,
          Type: client.product_typ_group,
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Clients");
        XLSX.writeFile(wb, "fast_moving_products.xlsx");
      },
    }));

    return (
      <>
        <LoadingIndicator show={getStatus === "pending"} />

        <Box sx={{ width: "100%" }}>
          <Paper sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
            <TableContainer>
              <Table
                sx={{ minWidth: 750 }}
                aria-label="fast moving products table"
              >
                <TableHead sx={{ background: "#04121f", color: "white" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FormatListNumberedOutlined /> S. No.
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CodeOutlined /> SKU Code
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BarChartOutlined /> Quotation
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BarChartOutlined /> Usage
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalOfferOutlined /> Brand
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CategoryOutlined /> Category
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SellOutlined /> MRP (₹)
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PaletteOutlined /> Finish
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <WidgetsOutlined /> Type
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movingProductPreview?.length > 0 ? (
                    movingProductPreview.map((product, index) => (
                      <TableRow
                        key={`${product.sku_code}-${index}-${product.usage_count}`}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#f9fafb" },
                          "&:hover": { backgroundColor: "#eef2ff" },
                          transition: "background-color 0.3s ease",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          navigate("/admin/sku-uses", {
                            state: {
                              sku: product.sku_code,
                              userOptions: userOptions,
                              user: user,
                              role,
                              from,
                              to,
                              month,
                              year,
                              selectedRegion,
                              selectedState,
                              selectedChannels
                            },
                          })
                        }
                      >
                        <TableCell>
                          <Box sx={{ fontWeight: "bold", color: "#6d28d9" }}>
                            {page * rowsPerPage + index + 1}
                          </Box>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {product.sku_code}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${product.quotation_count} Made`}
                            size="small"
                            sx={{
                              backgroundColor: "#e0f2fe",
                              color: "#0288d1",
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${product.usage_count} Used`}
                            size="small"
                            sx={{
                              backgroundColor: "#e0f2fe",
                              color: "#0288d1",
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                        <TableCell>{product.brand}</TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell>{product.mrp}</TableCell>
                        <TableCell>{product.finish}</TableCell>
                        <TableCell>{product.product_typ_group}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography
                          variant="subtitle1"
                          color="text.secondary"
                          sx={{ py: 2 }}
                        >
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, totalCount || 0]}
              component="div"
              count={totalCount || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
      </>
    );
  }
);

export default FastMovingProduct;
